import { ENV } from '@shared/config/env'

/**
 * Выбор бэкенда: основной или запасной.
 *
 * У части людей основной бэк закрыт по географии — он не отвечает вообще
 * никогда. Ждать таймаут на каждом запросе такому человеку нельзя, поэтому
 * удачное переключение запоминается в браузере.
 *
 * Но помнить его вечно нельзя тем более: если основной бэк ляжет разом у всех,
 * все разом запомнят запасной и останутся на нём навсегда — европейский сервер
 * такую нагрузку не держит. Поэтому память срочная и двухступенчатая, а на
 * старте приложения основной хост ещё и переспрашивается
 * (см. `probePrimaryHost`).
 */

const STORAGE_KEY = 'pauline:api-host'

/**
 * Первое переключение помним час: чаще всего это разовая сетевая яма, а не
 * недоступность. Час — потолок ущерба, если основной бэк лёг у всех сразу.
 */
const FIRST_SWITCH_TTL_MS = 60 * 60 * 1000

/**
 * Переключились повторно — значит дело не в яме, а в том, что основной хост у
 * этого человека закрыт. Такому спрашивать заново каждый час незачем.
 */
const REPEAT_SWITCH_TTL_MS = 24 * 60 * 60 * 1000

/**
 * За какой срок прошлое переключение ещё считается уликой. Два случая с
 * разницей в неделю — это два совпадения, а не закономерность.
 */
const ESCALATION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Таймаут первой попытки на основном хосте. Обычный таймаут запроса длиннее,
 * но здесь мы не ждём ответа — мы проверяем, жив ли хост, и человеку нельзя
 * показывать пустой экран полминуты.
 *
 * Пять секунд: выше времени нормального запроса даже на слабой мобильной сети,
 * но ещё в пределах терпения. Отказ соединения (refused, reset, DNS) до
 * таймаута вообще не доходит — там переключение мгновенное.
 */
export const PRIMARY_TIMEOUT_MS = 5000

export const PRIMARY_API_BASE_URL = ENV.API_BASE_URL
export const FALLBACK_API_BASE_URL = ENV.FALLBACK_API_BASE_URL

/** Запасной хост задан и отличается от основного. Иначе вся схема выключена. */
export const hasFallbackHost = FALLBACK_API_BASE_URL !== ''

interface HostMemory {
  /** До какого момента ходим на запасной хост. */
  until: number
  /** Какая это по счёту попытка уйти: от неё зависит срок. */
  level: 1 | 2
  /** Когда переключились. Запись переживает свой срок — она же и улика. */
  switchedAt: number
}

let memory: HostMemory | null = null
let useFallback = false

const readMemory = (): HostMemory | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as Partial<HostMemory>
    if (
      typeof saved.until !== 'number' ||
      typeof saved.switchedAt !== 'number'
    ) {
      return null
    }
    return {
      until: saved.until,
      level: saved.level === 2 ? 2 : 1,
      switchedAt: saved.switchedAt,
    }
  } catch {
    // Приватный режим или мусор в хранилище: работаем как при первом заходе.
    return null
  }
}

if (hasFallbackHost) {
  memory = readMemory()
  useFallback = memory !== null && memory.until > Date.now()
}

/** Хост, на который уходят запросы прямо сейчас. */
export const getActiveApiBaseUrl = (): string =>
  useFallback ? FALLBACK_API_BASE_URL : PRIMARY_API_BASE_URL

export const isFallbackActive = (): boolean => useFallback

/**
 * Запасной хост ответил — дальше идём сразу на него.
 *
 * Срок зависит от того, случалось ли это недавно: истёкшая запись из хранилища
 * не удаляется как раз ради этой проверки.
 */
export const rememberFallbackHost = (): void => {
  if (!hasFallbackHost) return

  const now = Date.now()
  const isRepeat =
    memory !== null && now - memory.switchedAt < ESCALATION_WINDOW_MS

  const next: HostMemory = {
    until: now + (isRepeat ? REPEAT_SWITCH_TTL_MS : FIRST_SWITCH_TTL_MS),
    level: isRepeat ? 2 : 1,
    switchedAt: now,
  }

  memory = next
  useFallback = true

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Без записи переключение проживёт до перезагрузки — это лучше, чем ничего.
  }
}

/**
 * Основной хост снова доступен — возвращаемся на него и забываем историю:
 * связь с основным есть, значит прошлые срывы были ямой, а не блокировкой.
 */
export const forgetFallbackHost = (): void => {
  memory = null
  useFallback = false
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* noop */
  }
}
